import {
  attachStateChange,
  getControlLabel,
  getElementMetadata,
  getElementType,
  hasActiveInteraction,
  toParamValue,
} from "./interactionMetadata";

const POPUPS =
  "[role='dialog'], [role='listbox'], .dropdown-menu, .p-overlaypanel, .p-sidebar";
const SELECT_PANELS =
  ".p-dropdown-panel, .p-multiselect-panel, .p-autocomplete-panel, .p-dropdown, .p-multiselect";
const ACCORDIONS = ".accordion-collapse, .p-accordion-content, details";
const SORTS = "th[aria-sort]";
const TOGGLES = "[role='switch'], [role='checkbox'], [role='radio']";
const SELECTS = ".p-dropdown, .p-multiselect";
const STATES = `${POPUPS}, ${ACCORDIONS}, ${SORTS}, ${TOGGLES}, ${SELECTS}`;

const isVisible = (element) => {
  for (let current = element; current; current = current.parentElement) {
    if (
      current.hidden ||
      current.getAttribute("aria-hidden") === "true" ||
      globalThis.getComputedStyle(current).display === "none"
    )
      return false;
  }
  return true;
};

const readState = (element, previous) => {
  if (element.matches(SELECTS)) {
    return (
      element.querySelector(".p-dropdown-label, .p-multiselect-label")
        ?.textContent || ""
    );
  }
  if (element.matches(SORTS))
    return element.getAttribute("aria-sort") || "none";
  if (element.matches(TOGGLES))
    return element.getAttribute("aria-checked") || "false";
  if (element.matches("details")) return element.open;
  if (element.matches(".accordion-collapse")) {
    if (element.classList.contains("collapsing")) return previous ?? false;
    return element.classList.contains("show");
  }
  if (element.matches(".p-accordion-content")) {
    return (
      isVisible(element) &&
      element
        .closest(".p-accordion-tab")
        ?.classList.contains("p-accordion-tab-active")
    );
  }
  return isVisible(element);
};

export const setupUIStateTracking = (track) => {
  const states = new Map();
  const identities = new WeakMap();
  const metadataFor = (element, type) => {
    if (!identities.has(element)) {
      identities.set(
        element,
        getElementMetadata(element, { element_type: type }),
      );
    }
    return identities.get(element);
  };
  const seed = () => {
    document.querySelectorAll(STATES).forEach((element) => {
      states.set(element, readState(element));
      metadataFor(
        element,
        element.matches(ACCORDIONS)
          ? "accordion"
          : element.matches(SORTS)
            ? "sort"
            : element.matches(TOGGLES)
              ? "toggle"
              : element.matches(SELECTS)
                ? getElementType(element)
                : "dialog",
      );
    });
  };

  const report = (element, previous, next) => {
    if (element.closest('[data-analytics-managed="true"]')) return;
    if (element.matches(SELECTS)) {
      if (previous === undefined) return;
      const type = getElementType(element);
      const params = metadataFor(element, type);
      const pageSize = type === "pagination";
      const label = element.querySelector(
        ".p-dropdown-label, .p-multiselect-label",
      );
      const empty = label?.matches(
        ".p-placeholder, .p-dropdown-label-empty, .p-multiselect-label-empty",
      );
      const tokens = element.querySelectorAll(".p-multiselect-token");
      const multi = element.matches(".p-multiselect");
      track(pageSize ? "page_size_changed" : "selection_changed", {
        ...params,
        ...(pageSize &&
          /^\d+$/.test(next.trim()) && { page_size: Number(next) }),
        ...(!pageSize && {
          selected_value: empty
            ? undefined
            : toParamValue(
                tokens.length
                  ? Array.from(tokens, (token) => token.textContent)
                  : next,
              ),
          ...(multi && {
            selected_count: empty
              ? 0
              : tokens.length ||
                element.querySelectorAll(
                  "option:checked, .p-multiselect-item.p-highlight",
                ).length ||
                undefined,
          }),
        }),
      });
    } else if (element.matches(SORTS)) {
      if (element.closest('[data-analytics-sort-managed="true"]')) return;
      if (previous === undefined || next === "none") return;
      const params = metadataFor(element, "sort");
      track("sort_changed", {
        ...params,
        sort_column: getControlLabel(element, "sort") || params.element_label,
        sort_direction: next,
        column_index: element.cellIndex,
      });
    } else if (element.matches(TOGGLES)) {
      // Native inputs report their change, including PrimeReact's input wrappers.
      if (
        previous === undefined ||
        element.matches("input") ||
        element.querySelector("input")
      )
        return;
      const params = metadataFor(element, "toggle");
      track("toggled", {
        ...params,
        checked: next === "true",
        selected_value: toParamValue(
          element.getAttribute("data-analytics-value") || params.element_label,
        ),
      });
    } else {
      // A select's option panel is part of the select; its change is reported once.
      if (element.closest(SELECT_PANELS)) return;
      const accordion = element.matches(ACCORDIONS);
      if (previous === undefined && !next) return;
      const action = `${accordion ? "accordion" : "popup"}_${next ? "opened" : "closed"}`;
      const params = metadataFor(element, accordion ? "accordion" : "dialog");
      // Side effect of a click: that click's single event carries this state.
      if (hasActiveInteraction()) {
        attachStateChange({ action, params, element });
        return;
      }
      track(action, params);
    }
  };

  seed();
  const observer = new MutationObserver(() => {
    const elements = new Set(document.querySelectorAll(STATES));
    elements.forEach((element) => {
      const previous = states.get(element);
      const next = readState(element, previous);
      states.set(element, next);
      if (previous !== next) report(element, previous, next);
    });
    states.forEach((previous, element) => {
      if (elements.has(element)) return;
      states.delete(element);
      if (previous === true) report(element, previous, false);
    });
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: [
      "class",
      "style",
      "hidden",
      "aria-hidden",
      "aria-sort",
      "aria-checked",
      "open",
    ],
  });
  return () => observer.disconnect();
};
